/**
 * The three event sections C17 adds — `countdown`, `schedule` and `tickets`
 * (FR-I17, [ADR-0036](../../../../docs/adr/0036-a-ninth-theme-dark-and-three-event-sections.md)
 * §2) — a reader and a **shared** renderer each, in the shape `library.tsx`
 * set for FR-I14's thirteen.
 *
 * ## Shared, and real, on all eight light themes
 *
 * ADR-0036 §3 is the one bounded exception to ADR-0026 §1: the eight existing
 * themes draw these three through the renderers below, and only Encore draws
 * them in its own markup. So these are designs rather than placeholders — laid
 * out in `BASE_CSS` on the palette's own custom properties, so a firm's colours
 * and each theme's paper, ink and rules reach them unasked.
 *
 * ## No script, ever
 *
 * The public site sends `script-src 'none'` (ADR-0013 §2), and none of this
 * asks it to change:
 *
 * - **The countdown is computed at render**, from `BlockContext.now` — the
 *   request's instant, since public pages are `no-store` — and does not tick.
 *   It compares instants, never calendar days, so spec §9-62's UTC-against-IST
 *   defect cannot reach it. Once the start has passed it draws the firm's
 *   *after* line, or nothing at all.
 * - **The schedule's day tabs are radio inputs and labels**, and CSS alone
 *   shows the checked day. On a phone there are no tabs: the days are stacked,
 *   each under its own heading.
 *
 * ## Nothing claimed that the firm did not type
 *
 * The days and hours are arithmetic on the firm's own instant; every word and
 * figure otherwise is the firm's. A ticket's price is the firm's string and is
 * **null** where the policy hides prices (spec §9-66) — read here a second
 * time after the service has already dropped it, as every price in this app
 * is. A tier's buttons go wherever the firm sends them: **there is no
 * checkout, no quantity and no cart** (CLAUDE.md §7).
 */

import type { ReactNode } from 'react'

import { list, obj, str } from '../read'
import { Buttons, readButtons, Section, type BlockContext, type SiteButton } from './common'

/* ── countdown ─────────────────────────────────────────────────────────── */

export interface CountdownView {
  heading: string | null
  /** The instant as the firm stored it — ISO 8601 with its offset. */
  startsAt: string
  place: string | null
  afterText: string | null
  /** C20, ADR-0041 §1 — the firm's buttons, zero to two. */
  buttons: SiteButton[]
  /** Whole days and hours to go at the render instant; `null` once it has passed. */
  remaining: { days: number; hours: number } | null
}

/**
 * The service's `datetime` shape exactly (`clean_datetime`): a date, a time
 * and **an offset**. A value without one is not read — guessing IST or UTC is
 * five and a half hours wrong for somebody.
 */
const INSTANT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})$/

/** The instant, or `null` when the value is not one this reader trusts. */
export function parseInstant(value: unknown): Date | null {
  const raw = str(value)
  if (!raw || !INSTANT.test(raw)) return null
  const at = new Date(raw)
  return Number.isNaN(at.getTime()) ? null : at
}

const HOUR = 60 * 60 * 1000

export function readCountdown(props: Record<string, unknown>, ctx: BlockContext): CountdownView | null {
  const startsAt = str(props.starts_at)
  const start = parseInstant(startsAt)
  if (!startsAt || !start) return null
  const now = ctx.now ?? new Date()
  const left = start.getTime() - now.getTime()
  const afterText = str(props.after_text)
  // At or after the instant, the event has begun: the firm's line, or nothing.
  if (left <= 0 && !afterText) return null
  const hours = left > 0 ? Math.floor(left / HOUR) : 0
  return {
    heading: str(props.heading),
    startsAt,
    place: str(props.place),
    afterText,
    buttons: readButtons(props.buttons, ctx),
    remaining: left > 0 ? { days: Math.floor(hours / 24), hours: hours % 24 } : null,
  }
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** India's offset from UTC, in minutes. IST keeps no daylight saving. */
const IST_MINUTES = 5 * 60 + 30

/**
 * The start, **always in IST** (C20, spec §10.12 item 5, T-4.82) — exactly
 * `D MMMM YYYY, hh:mmAM(IST)`: `2026-09-29T09:30+05:30` →
 * `29 September 2026, 09:30AM(IST)`. The hour is zero-padded, there is no
 * space before AM or PM, and `(IST)` follows at once. **No offset and no UTC
 * is ever printed** (the owner: *"no need to show +05:30, just IST"*).
 *
 * A value saved with another offset — before C20 the builder offered a
 * chooser — is the same instant, **converted** to IST rather than printed at
 * its own wall-clock time. The arithmetic is on the instant and a fixed
 * offset, never on the server's zone, so the render host's clock setting
 * cannot move it. Exported for the themes that redraw the section.
 */
export function formatStartsAt(iso: string): string {
  const at = parseInstant(iso)
  if (!at) return iso
  const ist = new Date(at.getTime() + IST_MINUTES * 60 * 1000)
  const hours24 = ist.getUTCHours()
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12
  const meridiem = hours24 < 12 ? 'AM' : 'PM'
  const hh = String(hours12).padStart(2, '0')
  const mm = String(ist.getUTCMinutes()).padStart(2, '0')
  const month = MONTHS[ist.getUTCMonth()]
  return `${ist.getUTCDate()} ${month} ${ist.getUTCFullYear()}, ${hh}:${mm}${meridiem}(IST)`
}

/** `1 day`, `3 days`. The unit is the renderer's; the figure is arithmetic. */
export function unit(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

export function Countdown(view: CountdownView): ReactNode {
  const { remaining, place, afterText, buttons } = view
  return (
    <Section className={`block countdown${remaining ? '' : ' countdown-after'}`} heading={view.heading}>
      {remaining ? (
        <p className="countdown-clock">
          <span className="countdown-unit">
            <span className="countdown-figure">{remaining.days}</span>
            <span className="countdown-label">{unit(remaining.days, 'day', 'days')}</span>
          </span>
          <span className="countdown-unit">
            <span className="countdown-figure">{remaining.hours}</span>
            <span className="countdown-label">{unit(remaining.hours, 'hour', 'hours')}</span>
          </span>
        </p>
      ) : (
        <p className="countdown-after-text">{afterText}</p>
      )}
      {remaining ? (
        <p className="countdown-when">
          <time dateTime={view.startsAt}>{formatStartsAt(view.startsAt)}</time>
          {place ? <span className="countdown-place"> · {place}</span> : null}
        </p>
      ) : place ? (
        <p className="countdown-when">{place}</p>
      ) : null}
      <Buttons className="countdown-actions" buttons={buttons} />
    </Section>
  )
}

/* ── schedule ──────────────────────────────────────────────────────────── */

export interface ScheduleSession {
  time: string
  title: string
  speaker: string | null
  place: string | null
  note: string | null
}

export interface ScheduleView {
  heading: string | null
  intro: string | null
  days: { label: string; sessions: ScheduleSession[] }[]
  /**
   * The name the day tabs' radio group shares and the prefix of their ids —
   * `sched<block index>`, unique on the page. **Additive to I-1**: two
   * schedules on one page must not share a radio group, and the renderer
   * cannot know its block's place without being told. The service refuses an
   * anchor under this prefix (`RESERVED_ANCHORS`), so the two cannot collide.
   */
  group: string
}

export function readSchedule(props: Record<string, unknown>, index = 0): ScheduleView | null {
  const days = list(props.days)
    .map((raw) => {
      const day = obj(raw)
      const label = str(day.label)
      if (!label) return null
      const sessions = list(day.sessions)
        .map((s) => {
          const session = obj(s)
          const time = str(session.time)
          const title = str(session.title)
          if (!time || !title) return null
          return {
            time,
            title,
            speaker: str(session.speaker),
            place: str(session.place),
            note: str(session.note),
          }
        })
        .filter((s): s is ScheduleSession => s !== null)
      // A day with no sessions is a tab with nothing behind it.
      return sessions.length > 0 ? { label, sessions } : null
    })
    .filter((d): d is NonNullable<typeof d> => d !== null)
    .slice(0, 7)
  if (days.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), days, group: `sched${index}` }
}

/**
 * Days as tabs on a wide screen and as a stack on a phone — **CSS only**. The
 * radios come first so `:checked ~` can reach both the tab row and the days;
 * one day draws no tabs at all. The first day is checked in the markup, so the
 * page is right before anyone touches it.
 */
export function Schedule(view: ScheduleView): ReactNode {
  const { days, intro, group } = view
  const tabbed = days.length > 1
  return (
    <Section className={`block schedule${tabbed ? ' schedule-tabbed' : ''}`} heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <div className="schedule-frame">
        {tabbed
          ? days.map((_, i) => (
              <input
                key={`r${i}`}
                type="radio"
                className="schedule-radio"
                name={group}
                id={`${group}-d${i}`}
                defaultChecked={i === 0}
              />
            ))
          : null}
        {tabbed ? (
          <div className="schedule-tablist">
            {days.map((day, i) => (
              <label key={`t${i}`} className="schedule-tab" htmlFor={`${group}-d${i}`}>
                {day.label}
              </label>
            ))}
          </div>
        ) : null}
        <div className="schedule-days">
          {days.map((day, i) => (
            <div className="schedule-day" key={i}>
              <h3 className="schedule-day-label">{day.label}</h3>
              <ol className="schedule-sessions">
                {day.sessions.map((s, j) => (
                  <li className="schedule-session" key={j}>
                    <p className="schedule-time">{s.time}</p>
                    <div className="schedule-body">
                      <p className="schedule-title">{s.title}</p>
                      {s.speaker || s.place ? (
                        <p className="schedule-meta">
                          {s.speaker}
                          {s.speaker && s.place ? ' · ' : null}
                          {s.place}
                        </p>
                      ) : null}
                      {s.note ? <p className="schedule-note">{s.note}</p> : null}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}

/* ── tickets ───────────────────────────────────────────────────────────── */

export interface TicketTier {
  name: string
  /** The firm's own string, or `null` — always `null` where the policy hides prices. */
  price: string | null
  perks: string[]
  badge: string | null
  soldOut: boolean
  /**
   * C20, ADR-0041 §1 — the tier's own buttons, zero to two: wherever the firm
   * sells, or a booking flow of its own. Never a checkout of ours, and never a
   * button the firm did not configure.
   */
  buttons: SiteButton[]
}

export interface TicketsView {
  heading: string | null
  intro: string | null
  tiers: TicketTier[]
}

export function readTickets(props: Record<string, unknown>, ctx: BlockContext): TicketsView | null {
  const tiers = list(props.tiers)
    .map((raw) => {
      const tier = obj(raw)
      const name = str(tier.name)
      if (!name) return null
      return {
        name,
        // Spec §9-66, for every profession alike. The service has already
        // dropped `price_text`; a payload from a fixture or a sample must not
        // draw one either.
        price: ctx.showPricing ? str(tier.price_text) : null,
        perks: list(tier.perks)
          .map((p) => str(obj(p).text))
          .filter((p): p is string => p !== null),
        badge: str(tier.badge),
        soldOut: tier.sold_out === true,
        buttons: readButtons(tier.buttons, ctx),
      }
    })
    .filter((t): t is TicketTier => t !== null)
    .slice(0, 6)
  if (tiers.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), tiers }
}

/**
 * The passes, side by side. A sold-out tier keeps its name and perks and loses
 * its buttons; a tier the firm gave no button has none. Since C20 every word
 * on a button is the firm's (ADR-0041 §1) — the *Get tickets* this section
 * used to write became the migrated button's label (`0039_section_buttons`).
 */
export function Tickets(view: TicketsView): ReactNode {
  const { tiers, intro } = view
  return (
    <Section className="block tickets" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="ticket-list">
        {tiers.map((tier, i) => (
          <li className={`ticket${tier.soldOut ? ' ticket-sold-out' : ''}`} key={i}>
            {tier.badge ? <p className="ticket-badge">{tier.badge}</p> : null}
            <h3 className="ticket-name">{tier.name}</h3>
            {tier.price ? <p className="ticket-price">{tier.price}</p> : null}
            {tier.perks.length > 0 ? (
              <ul className="ticket-perks">
                {tier.perks.map((perk, j) => (
                  <li key={j}>{perk}</li>
                ))}
              </ul>
            ) : null}
            {tier.soldOut ? (
              <p className="ticket-status">Sold out</p>
            ) : (
              <Buttons className="ticket-actions" buttons={tier.buttons} />
            )}
          </li>
        ))}
      </ul>
    </Section>
  )
}
